/**
 * Client-side contact-share filter for Text chat.
 * Blocks phone, email, URL, and social handles before send.
 * Server moderation remains the source of truth if this is bypassed.
 * Never log the matched text.
 */

import {
  hasBareEmailProviderHandle,
  hasBarePaymentProviderHandle,
  hasBareSocialProviderHandle,
} from './contactHandleShare';

export const CONTACT_SHARE_CATEGORIES = ['phone', 'email', 'url', 'socials', 'address', 'payment'];
export const POLICY_CATEGORIES = ['sexual_content', 'personal_info', 'harassment', 'other'];

/**
 * Map client contact signals to the locked Phase 1 category.
 * @param {string[]} [signals]
 * @returns {string[]}
 */
export const toPolicyCategories = (signals = []) => (signals.length > 0 ? ['personal_info'] : []);

const DIGIT_WORDS = {
  zero: '0',
  oh: '0',
  o: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
};

export const hasSpelledOutPhone = text => {
  const words = String(text || '')
    .toLowerCase()
    .match(/[a-z]+/g);
  if (!words) {
    return false;
  }
  let digits = '';
  words.forEach(word => {
    if (DIGIT_WORDS[word] != null) {
      digits += DIGIT_WORDS[word];
    } else if (digits.length > 0 && digits.length < 7) {
      digits = '';
    }
  });
  return digits.replace(/\D/g, '').length >= 10;
};

const CONTACT_DETECTORS = [
  {
    category: 'email',
    test: text =>
      /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(text) ||
      /\b[a-z0-9._%+-]+\s*(?:\(|\[)?\s*(?:at|@)\s*(?:\)|\])?\s*[a-z0-9-]+(?:\.[a-z0-9-]+)*(?:(?:\s*(?:\(|\[)?\s*dot\s*(?:\)|\])?\s*|\.)(?:com|net|org|edu|gov|io|co|me|us|uk|ca|info|biz|app|dev|xyz|ru|de|fr|au|in|ly|tv|live)|\s*(?:\(|\[)?\s*\.\s*(?:\)|\])?\s*com)\b/i.test(
        text
      ) ||
      hasBareEmailProviderHandle(text),
  },
  {
    category: 'phone',
    test: text =>
      /(?:\+|00)\d{1,3}[\s.-]?\(?\d{2,4}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}\b/.test(text) ||
      /\b(?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]*)\d{3}[\s.-]*\d{4}\b/.test(text) ||
      /\b\d{10}\b/.test(text) ||
      /\b(?:phone|mobile|cell|whatsapp|imessage|text me|call me)[:\s]+\+?\d[\d\s().-]{7,}\b/i.test(
        text
      ) ||
      hasSpelledOutPhone(text),
  },
  {
    category: 'url',
    test: text => {
      if (
        /\bhttps?:\/\/[^\s<>"']+/i.test(text) ||
        /\bwww\.[a-z0-9][a-z0-9.-]*\.[a-z]{2,}/i.test(text)
      ) {
        return true;
      }
      if (/\b(?:bit\.ly|tinyurl\.com|t\.co|goo\.gl)\/\S+/i.test(text)) {
        return true;
      }
      const withoutEmails = String(text || '').replace(
        /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi,
        ' '
      );
      return /\b[a-z0-9][a-z0-9-]{1,40}(?:(?:\s*(?:\(|\[)?\s*dot\s*(?:\)|\])?\s*|\.)(?:com|net|org|io|co|me|app)|\s*(?:\(|\[)?\s*\.\s*(?:\)|\])?\s*com)(?:\/\S*)?\b/i.test(
        withoutEmails
      );
    },
  },
  {
    category: 'socials',
    test: text =>
      /(?:instagram\.com\/|facebook\.com\/|fb\.com\/|twitter\.com\/|x\.com\/|tiktok\.com\/@|snapchat\.com\/add\/|linkedin\.com\/in\/|t\.me\/)/i.test(
        text
      ) ||
      /\b(?:my\s+)?(?:insta|instagram|snap|snapchat|facebook|fb|tiktok|twitter|discord|telegram|whatsapp)\s*(?:is|:|@)\s*@?[a-z0-9._]{2,}/i.test(
        text
      ) ||
      /\b(?:add me on|follow me on|dm me on|find me on)\s+(?:ig|insta|instagram|snap|snapchat|tiktok|facebook|twitter|discord|telegram)\b/i.test(
        text
      ) ||
      /(?:^|\s)@[a-z0-9._]{3,30}\b/i.test(text) ||
      hasBareSocialProviderHandle(text),
  },
  {
    category: 'address',
    test: text =>
      /\b\d{1,6}\s+[a-z0-9.'\s]{2,40}\s(?:street|st\.?|avenue|ave\.?|road|rd\.?|boulevard|blvd\.?|lane|ln\.?|drive|dr\.?|court|ct\.?|way|place|pl\.?|terrace|ter\.?)\b/i.test(
        text
      ),
  },
  {
    category: 'payment',
    test: text =>
      /(?:paypal\.me\/|venmo\.com\/|cash\.app\/\$?)/i.test(text) ||
      /\b(?:venmo|cashapp|cash app|zelle|paypal)\s*(?:is|:|@)\s*@?[a-z0-9._]{2,}/i.test(text) ||
      hasBarePaymentProviderHandle(text),
  },
];

/**
 * Category names only. Empty when the draft is safe to send.
 *
 * @param {string} [text]
 * @returns {string[]}
 */
export const detectContactShare = text => {
  const source = typeof text === 'string' ? text : '';
  if (!source.trim()) {
    return [];
  }
  return CONTACT_DETECTORS.filter(detector => detector.test(source)).map(
    detector => detector.category
  );
};

/**
 * True when Text chat should refuse send without hitting the other party.
 *
 * @param {string} [text]
 * @returns {boolean}
 */
export const isContactShareBlocked = text => detectContactShare(text).length > 0;
